import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

/* ===============================
   GET INVENTORY MOVEMENTS
================================ */
export const getInventoryMovements = createAsyncThunk(
  "inventory/getInventoryMovements",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/inventory/movements");
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ===============================
   GET BILL FOR CUSTOMER RETURN (INVOICE SEARCH)
   GET /inventory/bill-return?invoiceNumber=XXXX
================================ */
export const getBillForReturn = createAsyncThunk(
  "inventory/getBillForReturn",
  async (invoiceNumber, { rejectWithValue }) => {
    try {
      const res = await api.get("/inventory/bill-return", {
        params: { invoiceNumber },
      });
      return res.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ===============================
   CUSTOMER PRODUCT RETURN (POST)
================================ */
export const customerAddProductReturn = createAsyncThunk(
  "inventory/customerAddProductReturn",
  async ({ billId, productId, quantity, reason }, { rejectWithValue }) => {
    try {
      const res = await api.post(
        "/inventory/add/customer/product/return",
        {
          billId,
          productId,
          quantity,
          reason,
        }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ===============================
   GET RETURN PRODUCTS (LIST + PAGINATION)
================================ */
export const getReturnProducts = createAsyncThunk(
  "inventory/getReturnProducts",
  async ({ page = 1, search = "" }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        "/inventory/get/customer/product/returns",
        {
          params: { page, search },
        }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ===============================
   INVENTORY SLICE
================================ */
const inventorySlice = createSlice({
  name: "inventory",
  initialState: {
    movements: [],
    returns: [],
    billData: null,          // 🔥 invoice search result
    pagination: {},
    loading: false,
    error: null,
  },

  reducers: {
    clearInventoryState: (state) => {
      state.movements = [];
      state.returns = [];
      state.billData = null;
      state.pagination = {};
      state.loading = false;
      state.error = null;
    },

    clearBillForReturn: (state) => {
      state.billData = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ===== INVENTORY MOVEMENTS ===== */
      .addCase(getInventoryMovements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInventoryMovements.fulfilled, (state, action) => {
        state.loading = false;
        state.movements = action.payload;
      })
      .addCase(getInventoryMovements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== GET BILL FOR RETURN ===== */
      .addCase(getBillForReturn.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.billData = null;
      })
      .addCase(getBillForReturn.fulfilled, (state, action) => {
        state.loading = false;
        state.billData = action.payload;
      })
      .addCase(getBillForReturn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.billData = null;
      })

      /* ===== CUSTOMER RETURN ===== */
      .addCase(customerAddProductReturn.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(customerAddProductReturn.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(customerAddProductReturn.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== GET RETURNS LIST ===== */
      .addCase(getReturnProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReturnProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.returns = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getReturnProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearInventoryState,
  clearBillForReturn,
} = inventorySlice.actions;

export default inventorySlice.reducer;
